/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useRef, useState } from 'react';

import Loader from 'components/atoms/loader';
import AiManagementFilePreview from 'components/atoms/ai_management_file_preview';
import AiManagementDrawer from 'components/molecules/ai_management_drawer';
import { EditalFile, useEditalFiles } from 'hooks/useEditalFiles';
import { biddingFileHref } from 'services/actions/ai_management_files';
import { Body } from './styles';

export interface EditalPanelTarget {
  // Nome do arquivo e página que o Analista registrou para o trecho, quando há.
  arquivo?: string | null;
  pagina?: number | null;
}

interface AiManagementEditalPanelProps {
  biddingId: number;
  title: string;
  target: EditalPanelTarget;
  onClose: () => void;
}

const isPdf = (file: EditalFile) => /\.pdf$/i.test(file.name);

// O arquivo do trecho, quando o Analista registrou; senão o PDF que se chama "edital"; senão o
// primeiro PDF.
const arquivoInicial = (files: EditalFile[], arquivo?: string | null) => {
  const nome = (arquivo ?? '').trim().toLowerCase();
  return (
    (nome && files.find((file) => file.name.toLowerCase() === nome)) ||
    (nome && files.find((file) => file.name.toLowerCase().includes(nome))) ||
    files.find((file) => isPdf(file) && /edital/i.test(file.name)) ||
    files.find(isPdf) ||
    files[0] ||
    null
  );
};

const AiManagementEditalPanel: React.FC<AiManagementEditalPanelProps> = ({
  biddingId,
  title,
  target,
  onClose,
}) => {
  const { files, loading, error } = useEditalFiles(biddingId);
  const inicial = useMemo(() => arquivoInicial(files, target.arquivo), [files, target.arquivo]);
  const [selecionado, setSelecionado] = useState<string | null>(null);
  const corpo = useRef<HTMLDivElement>(null);

  // Sem fundo escuro (a proposta segue visível): o clique fora do painel o fecha.
  useEffect(() => {
    const fecharFora = (event: MouseEvent) => {
      const painel = corpo.current?.closest('aside');
      if (painel && !painel.contains(event.target as Node)) onClose();
    };
    document.addEventListener('mousedown', fecharFora, true);
    return () => document.removeEventListener('mousedown', fecharFora, true);
  }, [onClose]);

  useEffect(() => {
    setSelecionado(inicial?.key ?? null);
  }, [inicial]);

  const arquivo = files.find((file) => file.key === selecionado) ?? inicial;
  const pagina = arquivo && inicial && arquivo.key === inicial.key ? target.pagina : null;

  const renderBody = () => {
    if (loading) {
      return (
        <div className="py-4">
          <Loader />
        </div>
      );
    }
    if (error) {
      return (
        <div className="alert alert-warning m-4" role="alert">
          Não foi possível carregar os arquivos do edital.
        </div>
      );
    }
    if (!arquivo) {
      return (
        <p className="text-muted m-4">Nenhum arquivo de edital disponível para esta licitação.</p>
      );
    }
    return (
      <>
        <div className="edital-toolbar">
          {files.length > 1 ? (
            <select
              className="form-control form-control-sm"
              aria-label="Arquivo do edital"
              value={arquivo.key}
              onChange={(event) => setSelecionado(event.target.value)}
            >
              {files.map((file) => (
                <option key={file.key} value={file.key}>
                  {file.folder ? `${file.folder}/${file.name}` : file.name}
                </option>
              ))}
            </select>
          ) : (
            <span className="edital-file-name">{arquivo.name}</span>
          )}
          <a
            className="btn btn-sm btn-outline-primary"
            href={biddingFileHref(arquivo.downloadUrl)}
            download={arquivo.name}
            target="_blank"
            rel="noreferrer"
          >
            <i className="material-icons-outlined" aria-hidden="true">
              file_download
            </i>
            Baixar edital
          </a>
        </div>
        <div className="edital-preview">
          <AiManagementFilePreview
            key={arquivo.key}
            name={arquivo.name}
            downloadUrl={arquivo.downloadUrl}
            page={pagina}
          />
        </div>
      </>
    );
  };

  return (
    <AiManagementDrawer
      title={title}
      variant="side"
      bodyClassName="p-0 d-flex flex-column"
      onClose={onClose}
    >
      <Body ref={corpo}>{renderBody()}</Body>
    </AiManagementDrawer>
  );
};

export default AiManagementEditalPanel;
